/**
 * Sync Service - Gerencia sincronização de dados offline com o servidor
 */

import {
  getSyncQueue,
  removeFromSyncQueue,
  updateSyncQueueItem,
  getPendingSyncPatients,
  getAllPatientsLocal,
  getLocalDBStats
} from './indexedDB';
import axiosInstance from '@/utils/axios';

const MAX_RETRY_COUNT = 3;
const SYNC_BATCH_SIZE = 10;

// Status de sincronização
export const SyncStatus = {
  IDLE: 'idle',
  SYNCING: 'syncing',
  SUCCESS: 'success',
  ERROR: 'error',
  CONFLICT: 'conflict'
};

// Estado global de sincronização
let syncState = {
  status: SyncStatus.IDLE,
  progress: 0,
  total: 0,
  lastSync: null,
  errors: []
};

// Listeners para mudanças de estado
const listeners = new Set();

// Notificar listeners
function notifyListeners() {
  listeners.forEach(listener => listener({ ...syncState }));
}

// Adicionar listener
export function addSyncListener(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Obter estado atual
export function getSyncState() {
  return { ...syncState };
}

// Atualizar estado
function updateSyncState(updates) {
  syncState = { ...syncState, ...updates };
  notifyListeners();
}

/**
 * Sincronizar todos os dados pendentes
 */
export async function syncAll() {
  if (syncState.status === SyncStatus.SYNCING) {
    console.log('[Sync] Sync already in progress');
    return { success: false, message: 'Sincronização já em andamento' };
  }

  if (!navigator.onLine) {
    console.log('[Sync] Cannot sync - offline');
    return { success: false, message: 'Sem conexão com a internet' };
  }

  console.log('[Sync] Starting full sync...');
  updateSyncState({ status: SyncStatus.SYNCING, progress: 0, errors: [] });

  try {
    // Obter itens da fila
    const queue = await getSyncQueue();
    const stats = await getLocalDBStats();
    
    updateSyncState({ total: queue.length });

    if (queue.length === 0) {
      console.log('[Sync] No items to sync');
      updateSyncState({ 
        status: SyncStatus.SUCCESS, 
        lastSync: new Date().toISOString() 
      });
      return { success: true, message: 'Nenhum item para sincronizar' };
    }

    // Agrupar por tipo de entidade
    const patientItems = queue.filter(item => item.entity_type === 'patient');
    const prescriptionItems = queue.filter(item => item.entity_type === 'prescription');

    // Processar pacientes primeiro
    if (patientItems.length > 0) {
      await syncPatients(patientItems);
    }

    // Depois receitas
    if (prescriptionItems.length > 0) {
      await syncPrescriptions(prescriptionItems);
    }

    updateSyncState({ 
      status: SyncStatus.SUCCESS, 
      lastSync: new Date().toISOString() 
    });

    return { 
      success: true, 
      message: `${queue.length} itens sincronizados com sucesso` 
    };

  } catch (error) {
    console.error('[Sync] Error during sync:', error);
    updateSyncState({ 
      status: SyncStatus.ERROR,
      errors: [...syncState.errors, error.message]
    });
    return { success: false, message: error.message };
  }
}

/**
 * Sincronizar pacientes
 */
async function syncPatients(items) {
  console.log(`[Sync] Syncing ${items.length} patients...`);

  for (let i = 0; i < items.length; i += SYNC_BATCH_SIZE) {
    const batch = items.slice(i, i + SYNC_BATCH_SIZE);
    
    try {
      const response = await axiosInstance.post('/sync/patients', {
        records: batch.map(item => ({
          local_id: item.entity_local_id,
          action: item.action,
          version: item.payload.version,
          data: item.payload
        }))
      });

      // Processar resultados
      for (const result of response.data.results) {
        const queueItem = batch.find(b => b.entity_local_id === result.local_id);
        
        if (result.status === 'synced') {
          // Remover da fila
          await removeFromSyncQueue(queueItem.id);
          console.log(`[Sync] Patient synced: ${result.local_id} -> ${result.server_id}`);
        } else if (result.status === 'conflict') {
          // Marcar como conflito
          await updateSyncQueueItem(queueItem.id, {
            last_error: result.error,
            retry_count: MAX_RETRY_COUNT // Não tentar novamente
          });
          updateSyncState({
            errors: [...syncState.errors, `Conflito: ${result.error}`]
          });
        }
      }

      updateSyncState({ progress: syncState.progress + batch.length });

    } catch (error) {
      console.error('[Sync] Error syncing patient batch:', error);
      
      // Incrementar retry count
      for (const item of batch) {
        if (item.retry_count < MAX_RETRY_COUNT) {
          await updateSyncQueueItem(item.id, {
            retry_count: item.retry_count + 1,
            last_error: error.message
          });
        }
      }
    }
  }
}

/**
 * Sincronizar receitas
 */
async function syncPrescriptions(items) {
  console.log(`[Sync] Syncing ${items.length} prescriptions...`);

  for (let i = 0; i < items.length; i += SYNC_BATCH_SIZE) {
    const batch = items.slice(i, i + SYNC_BATCH_SIZE);
    
    try {
      const response = await axiosInstance.post('/sync/prescriptions', {
        records: batch.map(item => ({
          local_id: item.entity_local_id,
          action: item.action,
          version: item.payload.version,
          data: {
            patient_local_id: item.payload.patient_local_id,
            patient_server_id: item.payload.patient_server_id,
            medicamentos: item.payload.medicamentos,
            uso: item.payload.uso,
            medication_groups: item.payload.medication_groups,
            data_emissao: item.payload.data_emissao
          }
        }))
      });

      // Processar resultados
      for (const result of response.data.results) {
        const queueItem = batch.find(b => b.entity_local_id === result.local_id);
        
        if (result.status === 'synced') {
          // Remover da fila
          await removeFromSyncQueue(queueItem.id);
          console.log(`[Sync] Prescription synced: ${result.local_id} -> ${result.server_id}`);
        } else if (result.status === 'pending_patient') {
          // Paciente ainda não foi sincronizado, manter na fila
          console.log(`[Sync] Prescription pending patient sync: ${result.local_id}`);
        } else if (result.status === 'error') {
          // Erro ao sincronizar
          await updateSyncQueueItem(queueItem.id, {
            last_error: result.error,
            retry_count: queueItem.retry_count + 1
          });
          updateSyncState({
            errors: [...syncState.errors, `Erro receita: ${result.error}`]
          });
        }
      }

      updateSyncState({ progress: syncState.progress + batch.length });

    } catch (error) {
      console.error('[Sync] Error syncing prescription batch:', error);
      
      // Incrementar retry count
      for (const item of batch) {
        if (item.retry_count < MAX_RETRY_COUNT) {
          await updateSyncQueueItem(item.id, {
            retry_count: item.retry_count + 1,
            last_error: error.message
          });
        }
      }
    }
  }
}

/**
 * Verificar conflitos de homônimos antes de sincronizar
 */
export async function checkConflictsBeforeSync(patients) {
  const conflicts = [];

  for (const patient of patients) {
    try {
      // Verificar no servidor se existe homônimo
      const response = await axiosInstance.get('/patients/check-homonym', {
        params: {
          nome: patient.nome_original,
          data_nascimento: patient.data_nascimento
        }
      });

      if (response.data.has_homonym) {
        // Verificar se é realmente duplicata (mesmo nome normalizado + data)
        const serverMatches = response.data.matches.filter(m => 
          m.data_nascimento === patient.data_nascimento
        );

        if (serverMatches.length > 0 && !patient.server_id) {
          conflicts.push({
            local_patient: patient,
            server_matches: serverMatches,
            type: 'duplicate'
          });
        }
      }
    } catch (error) {
      console.error('[Sync] Error checking homonym:', error);
    }
  }

  return conflicts;
}

/**
 * Resolver conflito manualmente
 */
export async function resolveConflict(localId, resolution) {
  // resolution: 'keep_local', 'keep_server', 'merge'
  console.log(`[Sync] Resolving conflict for ${localId}: ${resolution}`);
  
  // Implementar lógica de resolução
  // ...
}

/**
 * Obter status de sincronização detalhado
 */
export async function getSyncStatus() {
  const stats = await getLocalDBStats();
  const queue = await getSyncQueue();
  
  return {
    ...syncState,
    pendingPatients: stats.patients_pending,
    pendingPrescriptions: stats.prescriptions_draft,
    queueSize: queue.length,
    queueItems: queue.map(item => ({
      id: item.id,
      type: item.entity_type,
      action: item.action,
      retries: item.retry_count,
      error: item.last_error
    }))
  };
}

/**
 * Forçar reprocessamento de itens com erro
 */
export async function retryFailedItems() {
  const queue = await getSyncQueue();
  const failedItems = queue.filter(item => 
    item.retry_count >= MAX_RETRY_COUNT || item.last_error
  );

  for (const item of failedItems) {
    await updateSyncQueueItem(item.id, {
      retry_count: 0,
      last_error: null
    });
  }

  console.log(`[Sync] Reset ${failedItems.length} failed items for retry`);
  return syncAll();
}

export default {
  syncAll,
  getSyncState,
  getSyncStatus,
  addSyncListener,
  checkConflictsBeforeSync,
  resolveConflict,
  retryFailedItems,
  SyncStatus
};
